import React, { useState, useEffect, useCallback } from 'react';
import { FaRuler, FaPlus, FaEdit, FaTimes, FaCalendarAlt, FaUser, FaSortAmountDown, FaSortAmountUp } from 'react-icons/fa';
import tallaService from '../services/tallaService';
import '../styles/InventoryManagement.css';

const TallaModal = ({ 
    isOpen, 
    onClose, 
    sociaId, 
    sociaNombre,
    onTallaCreated
}) => {
    // Estados principales
    const [tallas, setTallas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [editingTalla, setEditingTalla] = useState(null);
    const [filterMonth, setFilterMonth] = useState('');
    const [filterYear, setFilterYear] = useState('');
    const [sortOrder, setSortOrder] = useState('asc'); // 'asc' para antigua a reciente, 'desc' para reciente a antigua
    
    // Estado del formulario
    const [formData, setFormData] = useState({
        TALLA_CM: '',
        SOCIA_ID: sociaId || ''
    });
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    const loadTallasSocia = useCallback(async () => {
        setLoading(true);
        try {
            const result = await tallaService.getTallasBySocia(sociaId);
            if (result.success) {
                setTallas(result.data || []);
            }
        } catch (err) {
            console.error('Error al cargar tallas:', err);
        } finally {
            setLoading(false);
        }
    }, [sociaId]);

    // Cargar tallas de la socia cuando se abre el modal
    useEffect(() => {
        if (isOpen && sociaId) {
            console.log('TallaModal - Cargando tallas para socia ID:', sociaId);
            console.log('TallaModal - Nombre de la socia:', sociaNombre);
            loadTallasSocia();
            setFormData(prev => ({
                ...prev,
                SOCIA_ID: sociaId
            }));
        }
    }, [isOpen, sociaId, loadTallasSocia, sociaNombre]);

    // Validar formulario
    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.TALLA_CM || formData.TALLA_CM <= 0) {
            newErrors.TALLA_CM = 'La talla debe ser mayor a 0';
        }
        
        if (formData.TALLA_CM && (formData.TALLA_CM < 100 || formData.TALLA_CM > 250)) {
            newErrors.TALLA_CM = 'La talla debe estar entre 100 y 250 cm';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Manejar cambios en el formulario
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        // Limpiar error del campo
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    // Manejar envío del formulario
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) return;

        setSubmitting(true);
        try {
            let result;
            if (editingTalla) {
                // Actualizar talla existente
                result = await tallaService.updateTalla(editingTalla.TALLA_ID, formData);
            } else {
                // Crear nueva talla
                result = await tallaService.createTalla(formData);
                if (onTallaCreated) {
                    onTallaCreated(result.data);
                }
            }

            if (result.success) {
                await loadTallasSocia();
                handleCancelForm();
            }
        } catch (err) {
            console.error('Error al guardar talla:', err);
        } finally {
            setSubmitting(false);
        }
    };

    // Cancelar formulario
    const handleCancelForm = () => {
        setShowForm(false);
        setEditingTalla(null);
        setFormData({
            TALLA_CM: '',
            SOCIA_ID: sociaId || ''
        });
        setErrors({});
    };

    // Manejar edición
    const handleEdit = (talla) => {
        setEditingTalla(talla);
        setFormData({
            TALLA_CM: talla.TALLA_CM,
            SOCIA_ID: talla.SOCIA_ID
        });
        setShowForm(true);
    };

    // Filtrar y ordenar tallas por mes y año
    const filteredTallas = tallas.filter(talla => {
        const fechaTalla = new Date(talla.FECHA_REGISTRO);
        const month = fechaTalla.getMonth() + 1; // getMonth() returns 0-11
        const year = fechaTalla.getFullYear();
        
        const matchMonth = filterMonth === '' || month.toString() === filterMonth;
        const matchYear = filterYear === '' || year.toString() === filterYear;
        
        return matchMonth && matchYear;
    }).sort((a, b) => {
        const dateA = new Date(a.FECHA_REGISTRO);
        const dateB = new Date(b.FECHA_REGISTRO);
        
        if (sortOrder === 'asc') {
            return dateA - dateB; // Más antigua a más reciente
        } else {
            return dateB - dateA; // Más reciente a más antigua
        }
    });

    // Obtener años disponibles de las tallas
    const availableYears = [...new Set(tallas.map(talla => {
        return new Date(talla.FECHA_REGISTRO).getFullYear();
    }))].sort((a, b) => b - a); // Ordenar del más reciente al más antiguo

    // Manejar cierre del modal principal
    const handleClose = () => {
        if (submitting) return; // Prevenir cierre durante envío
        handleCancelForm();
        onClose();
    };

    // No renderizar si el modal no está abierto
    if (!isOpen) return null;

    return (
        <div 
            className="modal-overlay" 
            onClick={handleClose}
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 3000 // Mayor que los otros modales (1000 y 2000)
            }}
        >
            <div 
                className="modal-content" 
                onClick={(e) => e.stopPropagation()}
                style={{ 
                    maxWidth: '900px', 
                    width: '90%', 
                    maxHeight: '90vh', 
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)'
                }}
            >
                {/* Header */}
                <div style={{
                    background: '#9c2759',
                    color: 'white',
                    padding: '1.5rem',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderRadius: '12px 12px 0 0'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <FaRuler size={24} />
                        <div>
                            <h3 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '700' }}>
                                Gestión de Tallas
                            </h3>
                            <p style={{ margin: 0, fontSize: '0.9rem', opacity: 0.9 }}>
                                <FaUser style={{ marginRight: '0.5rem' }} />
                                {sociaNombre}
                            </p>
                        </div>
                    </div>
                    
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                            onClick={() => setShowForm(true)}
                            style={{
                                background: 'rgba(255, 255, 255, 0.2)',
                                color: 'white',
                                border: '2px solid rgba(255, 255, 255, 0.3)',
                                borderRadius: '8px',
                                padding: '0.5rem 1rem',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                            }}
                        >
                            <FaPlus /> Nueva Talla
                        </button>
                        <button
                            onClick={handleClose}
                            style={{
                                background: 'rgba(255, 255, 255, 0.2)',
                                color: 'white',
                                border: '2px solid rgba(255, 255, 255, 0.3)',
                                borderRadius: '8px',
                                padding: '0.5rem',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                            }}
                        >
                            <FaTimes />
                        </button>
                    </div>
                </div>

                {/* Contenido del modal */}
                <div style={{
                    flex: 1,
                    overflow: 'auto',
                    padding: '1.5rem'
                }}>
                    {/* Formulario */}
                    {showForm && (
                        <div style={{
                            marginBottom: '2rem',
                            padding: '1.5rem',
                            background: 'linear-gradient(135deg, #f8f9fa 0%, #fff3f4 100%)',
                            borderRadius: '12px',
                            border: '2px solid #9c2759'
                        }}>
                            <h4 style={{ 
                                margin: '0 0 1rem 0', 
                                color: '#9c2759',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                            }}>
                                <FaRuler />
                                {editingTalla ? 'Editar Talla' : 'Registrar Nueva Talla'}
                            </h4>
                            
                            <form onSubmit={handleSubmit}>
                                <div style={{ marginBottom: '1rem' }}>
                                    <label style={{ 
                                        display: 'block', 
                                        marginBottom: '0.5rem', 
                                        fontWeight: '600',
                                        color: '#495057'
                                    }}>
                                        Talla (cm)
                                    </label>
                                    <input
                                        type="number"
                                        name="TALLA_CM"
                                        value={formData.TALLA_CM}
                                        onChange={handleChange}
                                        placeholder="Ej: 165"
                                        min="100"
                                        max="250"
                                        step="0.1"
                                        style={{
                                            width: '100%',
                                            padding: '0.75rem',
                                            border: `2px solid ${errors.TALLA_CM ? '#dc3545' : '#d1b3c4'}`,
                                            borderRadius: '8px',
                                            fontSize: '1rem'
                                        }}
                                        required
                                    />
                                    {errors.TALLA_CM && (
                                        <div style={{ color: '#dc3545', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                                            {errors.TALLA_CM}
                                        </div>
                                    )}
                                </div>

                                <div style={{ display: 'flex', gap: '1rem' }}>
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        style={{
                                            background: submitting ? '#6c757d' : '#8a2050',
                                            color: 'white',
                                            border: 'none',
                                            padding: '0.75rem 1.5rem',
                                            borderRadius: '8px',
                                            cursor: submitting ? 'not-allowed' : 'pointer',
                                            fontSize: '1rem',
                                            fontWeight: '600',
                                            transition: 'all 0.2s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                            if (!submitting) e.target.style.background = '#9c2759';
                                        }}
                                        onMouseLeave={(e) => {
                                            if (!submitting) e.target.style.background = '#8a2050';
                                        }}
                                    >
                                        {submitting ? 'Guardando...' : (editingTalla ? 'Actualizar' : 'Guardar')}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleCancelForm}
                                        disabled={submitting}
                                        style={{
                                            background: '#6c757d',
                                            color: 'white',
                                            border: 'none',
                                            padding: '0.75rem 1.5rem',
                                            borderRadius: '8px',
                                            cursor: submitting ? 'not-allowed' : 'pointer',
                                            fontSize: '1rem',
                                            fontWeight: '600'
                                        }}
                                    >
                                        Cancelar
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    {/* Sección de tallas */}
                    <div>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '1.5rem'
                        }}>
                            <h4 style={{ margin: 0, color: '#9c2759', fontSize: '1.2rem' }}>
                                Historial de Tallas ({filteredTallas.length} de {tallas.length})
                            </h4>
                        </div>

                        {loading && (
                            <div style={{ 
                                textAlign: 'center', 
                                padding: '2rem',
                                color: '#6c757d'
                            }}>
                                Cargando tallas...
                            </div>
                        )}

                        {!loading && (
                            <>
                                {/* Filtros por mes y año */}
                                {tallas.length > 0 && (
                                    <div style={{
                                        marginBottom: '1.5rem',
                                        padding: '1.5rem',
                                        background: 'linear-gradient(135deg, #f8f9fa 0%, #fff3f4 100%)',
                                        borderRadius: '12px',
                                        border: '2px solid #d1b3c4',
                                        display: 'flex',
                                        gap: '1.5rem',
                                        alignItems: 'center',
                                        flexWrap: 'wrap',
                                        boxShadow: '0 4px 15px rgba(156, 39, 89, 0.1)'
                                    }}>
                                        <span style={{ 
                                            fontWeight: '700', 
                                            color: '#9c2759', 
                                            minWidth: 'fit-content',
                                            fontSize: '1rem',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.5rem'
                                        }}>
                                            <FaCalendarAlt /> Filtrar por:
                                        </span>
                                        
                                        <div style={{ display: 'flex', flexDirection: 'column', minWidth: '140px' }}>
                                            <label style={{ 
                                                fontSize: '0.85rem', 
                                                fontWeight: '600', 
                                                color: '#9c2759', 
                                                marginBottom: '0.5rem',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px'
                                            }}>Mes</label>
                                            <select
                                                value={filterMonth}
                                                onChange={(e) => setFilterMonth(e.target.value)}
                                                style={{
                                                    padding: '0.75rem',
                                                    border: '2px solid #d1b3c4',
                                                    borderRadius: '8px',
                                                    fontSize: '0.9rem',
                                                    backgroundColor: 'white',
                                                    color: '#495057',
                                                    fontWeight: '500',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s ease',
                                                    outline: 'none'
                                                }}
                                                onFocus={(e) => {
                                                    e.target.style.borderColor = '#9c2759';
                                                    e.target.style.boxShadow = '0 0 0 3px rgba(142, 36, 170, 0.1)';
                                                }}
                                                onBlur={(e) => {
                                                    e.target.style.borderColor = '#d1b3c4';
                                                    e.target.style.boxShadow = 'none';
                                                }}
                                            >
                                                <option value="">Todos</option>
                                                <option value="1">Enero</option>
                                                <option value="2">Febrero</option>
                                                <option value="3">Marzo</option>
                                                <option value="4">Abril</option>
                                                <option value="5">Mayo</option>
                                                <option value="6">Junio</option>
                                                <option value="7">Julio</option>
                                                <option value="8">Agosto</option>
                                                <option value="9">Septiembre</option>
                                                <option value="10">Octubre</option>
                                                <option value="11">Noviembre</option>
                                                <option value="12">Diciembre</option>
                                            </select>
                                        </div>

                                        <div style={{ display: 'flex', flexDirection: 'column', minWidth: '140px' }}>
                                            <label style={{ 
                                                fontSize: '0.85rem', 
                                                fontWeight: '600', 
                                                color: '#9c2759', 
                                                marginBottom: '0.5rem',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px'
                                            }}>Año</label>
                                            <select
                                                value={filterYear}
                                                onChange={(e) => setFilterYear(e.target.value)}
                                                style={{
                                                    padding: '0.75rem',
                                                    border: '2px solid #d1b3c4',
                                                    borderRadius: '8px',
                                                    fontSize: '0.9rem',
                                                    backgroundColor: 'white',
                                                    color: '#495057',
                                                    fontWeight: '500',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s ease',
                                                    outline: 'none'
                                                }}
                                                onFocus={(e) => {
                                                    e.target.style.borderColor = '#9c2759';
                                                    e.target.style.boxShadow = '0 0 0 3px rgba(142, 36, 170, 0.1)';
                                                }}
                                                onBlur={(e) => {
                                                    e.target.style.borderColor = '#d1b3c4';
                                                    e.target.style.boxShadow = 'none';
                                                }}
                                            >
                                                <option value="">Todos</option>
                                                {availableYears.map(year => (
                                                    <option key={year} value={year.toString()}>{year}</option>
                                                ))}
                                            </select>
                                        </div>

                                        {(filterMonth !== '' || filterYear !== '') && (
                                            <button
                                                onClick={() => {
                                                    setFilterMonth('');
                                                    setFilterYear('');
                                                }}
                                                style={{
                                                    padding: '0.75rem 1.5rem',
                                                    background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)',
                                                    color: 'white',
                                                    border: '2px solid #dc3545',
                                                    borderRadius: '8px',
                                                    fontSize: '0.85rem',
                                                    fontWeight: '600',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.3s ease',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.5px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '0.5rem',
                                                    boxShadow: '0 2px 8px rgba(220, 53, 69, 0.3)'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.target.style.background = 'linear-gradient(135deg, #c82333 0%, #a71e2a 100%)';
                                                    e.target.style.transform = 'translateY(-2px)';
                                                    e.target.style.boxShadow = '0 4px 15px rgba(220, 53, 69, 0.4)';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.target.style.background = 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)';
                                                    e.target.style.transform = 'translateY(0)';
                                                    e.target.style.boxShadow = '0 2px 8px rgba(220, 53, 69, 0.3)';
                                                }}
                                            >
                                                <FaTimes /> Limpiar Filtros
                                            </button>
                                        )}
                                    </div>
                                )}

                                {/* Botones de ordenamiento */}
                                {tallas.length > 0 && (
                                    <div style={{
                                        display: 'flex',
                                        gap: '0.5rem',
                                        marginBottom: '1.5rem',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        padding: '1rem',
                                        background: 'linear-gradient(135deg, #fff3e0 0%, #fce4ec 100%)',
                                        borderRadius: '10px',
                                        border: '1px solid #f8bbd9'
                                    }}>
                                        <span style={{ 
                                            fontSize: '0.9rem', 
                                            fontWeight: '600', 
                                            color: '#9c2759',
                                            minWidth: 'fit-content'
                                        }}>
                                            Ordenar por fecha:
                                        </span>
                                        <button
                                            onClick={() => setSortOrder('asc')}
                                            style={{
                                                padding: '0.6rem 1rem',
                                                background: sortOrder === 'asc' ? 'linear-gradient(135deg, #9c2759 0%, #8a2050 100%)' : 'white',
                                                color: sortOrder === 'asc' ? 'white' : '#9c2759',
                                                border: `2px solid ${sortOrder === 'asc' ? '#9c2759' : '#d1b3c4'}`,
                                                borderRadius: '8px',
                                                fontSize: '0.85rem',
                                                fontWeight: '600',
                                                cursor: 'pointer',
                                                transition: 'all 0.3s ease',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.4rem',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px',
                                                boxShadow: sortOrder === 'asc' ? '0 3px 12px rgba(142, 36, 170, 0.3)' : '0 2px 8px rgba(0,0,0,0.1)'
                                            }}
                                            onMouseEnter={(e) => {
                                                if (sortOrder !== 'asc') {
                                                    e.target.style.background = 'linear-gradient(135deg, #f8f9fa 0%, #fff3f4 100%)';
                                                    e.target.style.borderColor = '#9c2759';
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                if (sortOrder !== 'asc') {
                                                    e.target.style.background = 'white';
                                                    e.target.style.borderColor = '#d1b3c4';
                                                }
                                            }}
                                        >
                                            <FaSortAmountUp />
                                            Antigua → Reciente
                                        </button>
                                        <button
                                            onClick={() => setSortOrder('desc')}
                                            style={{
                                                padding: '0.6rem 1rem',
                                                background: sortOrder === 'desc' ? 'linear-gradient(135deg, #9c2759 0%, #8a2050 100%)' : 'white',
                                                color: sortOrder === 'desc' ? 'white' : '#9c2759',
                                                border: `2px solid ${sortOrder === 'desc' ? '#9c2759' : '#d1b3c4'}`,
                                                borderRadius: '8px',
                                                fontSize: '0.85rem',
                                                fontWeight: '600',
                                                cursor: 'pointer',
                                                transition: 'all 0.3s ease',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.4rem',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px',
                                                boxShadow: sortOrder === 'desc' ? '0 3px 12px rgba(142, 36, 170, 0.3)' : '0 2px 8px rgba(0,0,0,0.1)'
                                            }}
                                            onMouseEnter={(e) => {
                                                if (sortOrder !== 'desc') {
                                                    e.target.style.background = 'linear-gradient(135deg, #f8f9fa 0%, #fff3f4 100%)';
                                                    e.target.style.borderColor = '#9c2759';
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                if (sortOrder !== 'desc') {
                                                    e.target.style.background = 'white';
                                                    e.target.style.borderColor = '#d1b3c4';
                                                }
                                            }}
                                        >
                                            <FaSortAmountDown />
                                            Reciente → Antigua
                                        </button>
                                    </div>
                                )}

                                {/* Lista de tallas */}
                                {filteredTallas.length > 0 ? (
                                    <div className="tallas-grid" style={{ display: 'grid', gap: '1rem', marginBottom: '2rem' }}>
                                        {filteredTallas.map((talla) => (
                                            <div 
                                                key={talla.TALLA_ID} 
                                                className="talla-card"
                                                style={{
                                                    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                                                    border: '2px solid #d1b3c4',
                                                    borderRadius: '12px',
                                                    padding: '1rem',
                                                    boxShadow: '0 4px 12px rgba(123, 31, 162, 0.1)',
                                                    transition: 'all 0.3s ease',
                                                    position: 'relative'
                                                }}
                                            >
                                                <div style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'flex-start',
                                                    marginBottom: '0.75rem'
                                                }}>
                                                    <div>
                                                        <h5 style={{
                                                            margin: '0 0 0.5rem 0',
                                                            color: '#9c2759',
                                                            fontSize: '1.1rem',
                                                            fontWeight: '700'
                                                        }}>
                                                            {talla.TALLA_ID}
                                                        </h5>
                                                        <p style={{
                                                            margin: 0,
                                                            color: '#666',
                                                            fontSize: '0.9rem',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '0.3rem'
                                                        }}>
                                                            <FaCalendarAlt size={12} />
                                                            {new Date(talla.FECHA_REGISTRO).toLocaleDateString('es-ES', {
                                                                year: 'numeric',
                                                                month: 'long',
                                                                day: 'numeric'
                                                            })}
                                                        </p>
                                                    </div>
                                                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                        <button
                                                            onClick={() => handleEdit(talla)}
                                                            style={{
                                                                background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
                                                                color: 'white',
                                                                border: 'none',
                                                                borderRadius: '6px',
                                                                padding: '0.4rem 0.8rem',
                                                                cursor: 'pointer',
                                                                fontSize: '0.8rem',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '0.3rem',
                                                                transition: 'all 0.2s ease'
                                                            }}
                                                        >
                                                            <FaEdit size={12} />
                                                            Editar
                                                        </button>
                                                    </div>
                                                </div>

                                                <div style={{
                                                    background: 'rgba(142, 36, 170, 0.1)',
                                                    borderRadius: '8px',
                                                    padding: '0.75rem',
                                                    textAlign: 'center'
                                                }}>
                                                    <div style={{
                                                        fontSize: '1.8rem',
                                                        fontWeight: '700',
                                                        color: '#9c2759',
                                                        marginBottom: '0.25rem'
                                                    }}>
                                                        {talla.TALLA_CM} cm
                                                    </div>
                                                    <div style={{
                                                        fontSize: '0.8rem',
                                                        color: '#666',
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.5px'
                                                    }}>
                                                        Estatura
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div style={{
                                        textAlign: 'center',
                                        padding: '3rem 2rem',
                                        color: '#6c757d',
                                        background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                                        borderRadius: '12px',
                                        border: '2px dashed #dee2e6'
                                    }}>
                                        <FaRuler size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
                                        <h4 style={{ margin: '0 0 0.5rem 0' }}>
                                            {tallas.length === 0 ? 'No hay tallas registradas' : 'No se encontraron tallas con los filtros aplicados'}
                                        </h4>
                                        <p style={{ margin: 0, fontSize: '0.9rem' }}>
                                            {tallas.length === 0 
                                                ? 'Registra la primera talla de la socia para comenzar el seguimiento.'
                                                : 'Intenta cambiar los filtros o limpiarlos para ver más resultados.'
                                            }
                                        </p>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TallaModal;
